import { useState } from 'react';
import { api } from '../lib/api';
import { Badge, IssueList, Loading, Toast, useAsync } from '../components/common';
import type { Principal, ValidationResult } from '../types';

export default function PublishPage({ principal }: { principal: Principal }) {
  const { data, error, loading, reload } = useAsync(() => api.publications(), []);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [snapshot, setSnapshot] = useState<unknown>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; tone: 'info' | 'error' } | null>(null);

  async function run(label: string, fn: () => Promise<void>) {
    setBusy(label);
    try {
      await fn();
    } catch (err) {
      setToast({ message: (err as Error).message, tone: 'error' });
    } finally {
      setBusy(null);
    }
  }

  const errorCount = validation?.issues.filter((i) => i.severity === 'error').length ?? 0;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Validate &amp; publish</h1>
          <p>
            Publishing writes a complete snapshot to S3. Clients always replace their view with it;
            nothing merges it over a built-in default, because a merge means a deleted key silently
            keeps its old value.
          </p>
        </div>
      </div>

      <div className="card">
        <h2>Validate the whole estate</h2>
        <div className="actions" style={{ marginBottom: 14 }}>
          <button
            onClick={() => run('validate', async () => setValidation(await api.validateEstate()))}
            disabled={busy !== null}
          >
            {busy === 'validate' ? 'Checking…' : 'Run validation'}
          </button>
          <button
            onClick={() => run('preview', async () => setSnapshot(await api.snapshotPreview()))}
            disabled={busy !== null}
          >
            {busy === 'preview' ? 'Rendering…' : 'Preview snapshot'}
          </button>
        </div>
        {validation && <IssueList issues={validation.issues} />}
      </div>

      {snapshot !== null && (
        <div className="card">
          <h2>
            Snapshot preview
            <span className="hint">exactly the bytes clients would receive</span>
          </h2>
          <pre className="json">{JSON.stringify(snapshot, null, 2)}</pre>
        </div>
      )}

      {principal.role === 'admin' && (
        <div className="card">
          <h2>Publish</h2>
          <label className="field">
            <span>Note for the publication history</span>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Onboard ACCS and Greenwise"
            />
          </label>
          <div className="actions">
            <button
              className="primary"
              disabled={busy !== null || errorCount > 0}
              onClick={() =>
                run('publish', async () => {
                  const published = await api.publish(note);
                  setToast({ message: `Published v${published.version}.`, tone: 'info' });
                  setNote('');
                  reload();
                })
              }
            >
              {busy === 'publish' ? 'Publishing…' : 'Publish snapshot'}
            </button>
          </div>
          {errorCount > 0 && (
            <div className="callout danger" style={{ marginTop: 12 }}>
              {errorCount} validation {errorCount === 1 ? 'error' : 'errors'} must be resolved first.
            </div>
          )}
        </div>
      )}

      <div className="card">
        <h2>Publication history</h2>
        {loading || !data ? (
          <Loading error={error} />
        ) : data.length === 0 ? (
          <div className="callout info">Nothing published yet.</div>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Published</th>
                  <th>By</th>
                  <th>Parents</th>
                  <th>Lambdas</th>
                  <th>Bindings</th>
                  <th>Checksum</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {data.map((p, index) => (
                  <tr key={p.version}>
                    <td>
                      <Badge tone={index === 0 ? 'ok' : 'default'}>v{p.version}</Badge>
                    </td>
                    <td className="small nowrap">{p.published_at}</td>
                    <td className="small">{p.published_by}</td>
                    <td>{p.parent_count}</td>
                    <td>{p.lambda_count}</td>
                    <td>{p.binding_count}</td>
                    <td className="mono small">{p.checksum_sha256.slice(0, 12)}…</td>
                    <td className="small">{p.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {toast && <Toast {...toast} onDone={() => setToast(null)} />}
    </>
  );
}
